import React, { forwardRef, memo, useImperativeHandle, useState } from 'react';

import { useDisclosure } from '@chakra-ui/react';

import { updateWashHub } from 'api/location';
import { useMutation, useQueryClient } from 'react-query';

import { successToast } from 'utils/successToast';

import { washHub } from 'store/slices';
import { useAppDispatch } from 'store/hooks';

import { Location } from 'types/washhub';
import { RefType, EditWashHubModalProps } from './EditWashHubModal.props';

import EditWashHubModalView from './EditWashHubModal.view';

const Container = forwardRef<RefType, EditWashHubModalProps>((_, ref) => {
  const dispatch = useAppDispatch();
  const queryClient = useQueryClient();

  const { isOpen, onOpen, onClose } = useDisclosure();

  const { mutate, isLoading } = useMutation(updateWashHub);

  const selectedWashHub = washHub.selectors.useSelectedWashHub();

  const [selectedHub, setSelectedHub] = useState<Location | null>(null);

  useImperativeHandle(
    ref,
    () => ({
      onOpen: (_washHub) => {
        setSelectedHub(_washHub);

        onOpen();
      },
    }),
    [onOpen, setSelectedHub],
  );

  const _props = {
    isOpen,
    onClose,
    isLoading,
    selectedHub,
    onUpdate: (payload: any) => {
      mutate(
        {
          _id: payload?._id,
          name: payload?.name,
          email: payload?.email,
          address: payload?.address,
          size: payload?.hubSize,
          packages: payload?.packages,
          phcMethod: payload?.phcMethod,
        },
        {
          onSuccess: async () => {
            successToast('Wash Hub updated successfully');

            onClose();

            const queryKey = 'wash-hub-locations';

            queryClient.refetchQueries(queryKey, {
              exact: true,
            });
            queryClient.invalidateQueries({
              queryKey: [queryKey],
            });

            queryClient.invalidateQueries({
              queryKey: [`fetch-all-sku-select`],
            });

            const washHubsData =
              queryClient.getQueryData<Location[]>(queryKey) || [];

            const _selectedWashHub = washHubsData?.find(
              (w) => w._id === selectedWashHub?._id,
            );

            if (_selectedWashHub) {
              const updatedWashHub = {
                ..._selectedWashHub,
                phcMethod: payload.phcMethod,
              };
              dispatch(
                washHub.WashhubActions.setSelectedLocation(updatedWashHub),
              );
            }
          },
        },
      );
    },
  };

  return <EditWashHubModalView {..._props} />;
});

const EditWashHubModal = memo(Container);

export default EditWashHubModal;
